import { Injectable } from "@angular/core";
import { ServiceMessageBrokerFactory } from "shared/provider-webworker";
import { ScratchPadDataLookupService } from "./ScratchPadDataLookupService";
import {
  PrimaryBlotterLookupService,
  ExtendedOrderEntryLogType,
} from "../../impl/PrimaryBlotterLookupService";
import { IScratchPadDataPromiseLookupService } from "../IScratchPadDataPromiseLookupService";
import { RawDataScratchPadAdapter } from "../../../mediator/adapter/RawDataScratchPadAdapter";

import { CubiqWorker } from "../../../worker/CubiqWorker";
import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { UserLookupService } from "./UserLookupService";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { ActiveUserProfilePromiseService } from "shared/auth-services";
import { ActiveUser } from "../../../model/core/ActiveUser";
import {
  COLUMN_SALES_GROUP_ID,
  COLUMN_ENTRY_USER_ID,
  COLUMN_DESK_ID,
} from "shared/cubiq-services/model/OrdersTableDefinitionFactory.constants";
import { TraderGroupLookupService } from "./TraderGroupLookupService";
import { CcyPairLookupService } from "./CcyPairLookupService";
import { NotificationService } from "../../notification/impl/NotificationService";
import { ClientLookupService } from "./ClientLookupService";
import { TraderGroupPromiseLookupService } from "./TraderGroupPromiseLookupService";
import {
  SUSPENSION_PARAM_NAME,
  SUSPENSION_PARAM_TYPE,
} from "shared/cubiq-services/model/orders/SuspensionHelper";
import { DealtPricingService } from "../../pricing/impl/DealtPricingService";

export const BLOTTERS_SCRATCH_PAD_ORDERS: string[] = ["Scratch Pad Orders"];

const SavedRequestType = {
  PRIME: Symbol("PRIME"),
  ORDER_ENTRY: Symbol("ORDER_ENTRY"),
  IMPORT: Symbol("IMPORT"),
} as const;
type SavedRequestType = (typeof SavedRequestType)[keyof typeof SavedRequestType];

const VISIBLE_ORDER_STATES: Array<CubiqTS.OrderState> = [
  CubiqTS.OrderState.SAVED,
  CubiqTS.OrderState.ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.MODIFY_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.DI_NEEDED,
  CubiqTS.OrderState.REJECTED,
  CubiqTS.OrderState.SALES_REJECTED,
  CubiqTS.OrderState.TRADER_REJECTED,
  CubiqTS.OrderState.EXT_ACCEPTANCE_NEEDED,
  CubiqTS.OrderState.EXT_MODIFY_ACCEPTANCE_NEEDED,
];

/**
 * Data service for all Scratchpad orders, both Saved and transient. All orders are
 * mapped by their external reference.
 * The underlying request data items are assumed to be immutable - copying an order will
 * copy the top-level data but the nested data is shallow copied e.g. allocations so local
 * data operations should be restricting to adding/removing items only
 */
@Injectable()
export class ScratchPadDataPrimaryLookupService
  extends PrimaryBlotterLookupService<
    string,
    CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl
  >
  implements IScratchPadDataPromiseLookupService
{
  protected readonly ORDER_DATA: CubiqTS.ConcreteEncodable;

  private activeUser: ActiveUser;
  protected orderEntryReferences: Set<string> = new Set();
  protected traderGroupLookup: TraderGroupLookupService;

  constructor(
    protected base: ScratchPadDataLookupService,
    factory: ServiceMessageBrokerFactory,
    cubiqWorker: CubiqWorker,
    dataAdapter: RawDataScratchPadAdapter,
    private activeUserService: ActiveUserProfilePromiseService,
    private traderGroupService: TraderGroupPromiseLookupService,
    private currencyPairLookup: CcyPairLookupService,
    private userLookup: UserLookupService,
    private clientLookupService: ClientLookupService,
    protected notifService: NotificationService,
    private pricingService: DealtPricingService,
  ) {
    super(
      base,
      factory,
      [CubiqTS.LinkedOrderRequestImpl, CubiqTS.FixingOrderRequestImpl] as any,
      cubiqWorker,
      "LinkedOrderRequestImpl",
      CubiqTS.SavedOrderEventImpl,
      dataAdapter,
      notifService,
    );
    this.ORDER_DATA = CubiqTS.ENCODABLE(CubiqTS.OrderDataImpl);

    logger.out.info("scrdpl-s - primeDataProfile waiting on isAuth()");

    this.traderGroupLookup = this.traderGroupService.getBase();

    Promise.all([
      cubiqWorker.isAuth(`${this.constructor.name}`),
      // this.privatizationService.isReady(),
      this.activeUserService.getActiveUserData(),
      this.traderGroupService.isPrimed(),
    ]).then(([_, activeUser, __]) => {
      this.activeUser = activeUser;

      cubiqWorker
        .primeDataProfile(BLOTTERS_SCRATCH_PAD_ORDERS, CubiqTS.LinkedOrderRequestImpl.name)
        .subscribe(
          (savedOrderData: CubiqTS.LinkedOrderRequestImpl[]) => {
            if (savedOrderData == null || Array.isArray(savedOrderData) == false) {
              savedOrderData = [];
            }
            console.debug("ScratchPadDataPrimaryLookupService: primeDataProfile isAuth() resolved");
            logger.out.info(`scrdpl-s - Priming scratch pad with ${savedOrderData.length} orders`);
            this.base.prime(
              savedOrderData.map((req) => this.processSavedRequest(req, SavedRequestType.PRIME)),
              false,
            );
          },
          (e: Error) => {
            logger.out.error("scrdpl-s - Failed to prime order data: " + e);
            this._primed.reject(e);
          },
          () => {
            this.base.primeCompleted();
            this._primed.resolve(true);
          },
        );
    });

    this.cubiqWorker
      .getEncodableObservable(CubiqTS.SavedOrderEventImpl.name)
      .subscribe((event: CubiqTS.SavedOrderEventImpl) => {
        if (event != null) {
          this.logOrderEntryDebugInfo(event, "PROCESS");
          this.processSavedOrderEvent(event);
          this.processAndFireOrderEntryEvent(event);
        }
      });

    this.cubiqWorker
      .getEncodableObservable(CubiqTS.SavedOrderEventListImpl.name)
      .subscribe((event: CubiqTS.SavedOrderEventListImpl) => {
        if (event != null) {
          this.logOrderEntryDebugInfo(event, "PROCESS");
          this.processSavedOrderEventList(event);
        }
      });

    this.cubiqWorker
      .getEncodableObservable(CubiqTS.BulkOrderLifeCycleEventImpl.name)
      .subscribe((event: CubiqTS.BulkOrderLifeCycleEventImpl) => {
        if (event != null) {
          this.processBulkOrderLifecycleEvent(event);
        }
      });

    this.cubiqWorker
      .getEncodableObservable(CubiqTS.OrderLifeCycleEventImpl.name)
      .subscribe((event: CubiqTS.OrderLifeCycleEventImpl) => {
        if (event != null) {
          this.processOrderLifecycleEvent(event);
        }
      });
  }

  private processSavedRequest(
    req: CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl,
    type: SavedRequestType,
  ): CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl {
    if (
      req instanceof CubiqTS.LinkedOrderRequestImpl &&
      req.rootOrder != null &&
      (type == SavedRequestType.ORDER_ENTRY ||
        (type == SavedRequestType.PRIME && !Object.hasOwn(req.rootOrder, "orderState")))
    ) {
      req.rootOrder["orderState"] = req.rootOrder["state"] = CubiqTS.OrderState.SAVED;
    } else if (req instanceof CubiqTS.FixingOrderRequestImpl && req.fixingOrderData != null) {
      if (
        type == SavedRequestType.ORDER_ENTRY ||
        (type == SavedRequestType.PRIME && !Object.hasOwn(req.fixingOrderData, "orderState"))
      ) {
        req.fixingOrderData["orderState"] = req.fixingOrderData["state"] = CubiqTS.OrderState.SAVED;
      }

      // restore markup from first allocation
      if (
        Array.isArray(req.fixingOrderData.allocations) &&
        req.fixingOrderData.allocations.length > 0 &&
        req.fixingOrderData.markupType == null
      ) {
        let allocation = req.fixingOrderData.allocations[0];
        req.fixingOrderData.markupType = allocation.markupType;
        req.fixingOrderData.markup = allocation.markupQty;
      }
    }
    return req;
  }

  private processSavedOrderEvent(event: CubiqTS.SavedOrderEventImpl) {
    let req: CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl;

    if (event.orderDataRequest) {
      req = event.orderDataRequest;
    }
    if (event.fixingOrderRequest) {
      req = event.fixingOrderRequest;
    }
    req = this.processSavedRequest(req, SavedRequestType.ORDER_ENTRY);
    this.base.mapItem(req);
    this.fireModelUpdateEvent(req);
  }

  public processSavedOrderEventList(event: CubiqTS.SavedOrderEventListImpl) {
    if (
      event.orderDataRequests &&
      Array.isArray(event.orderDataRequests) &&
      event.orderDataRequests.length > 0
    ) {
      event.orderDataRequests.forEach((orderDataRequest) => {
        let req = this.processSavedRequest(orderDataRequest, SavedRequestType.IMPORT);
        this.base.mapItem(req);
      });
    }
    if (
      event.fixingOrderRequests &&
      Array.isArray(event.fixingOrderRequests) &&
      event.fixingOrderRequests.length > 0
    ) {
      event.fixingOrderRequests.forEach((fixingOrderRequest) => {
        let req = this.processSavedRequest(fixingOrderRequest, SavedRequestType.IMPORT);
        this.base.mapItem(req);
      });
    }
    this.fireModelUpdateEvent(event);
  }

  public processBulkOrderLifecycleEvent(event) {
    if (event.events && Array.isArray(event.events)) {
      event.events.forEach((ev: CubiqTS.OrderLifeCycleEventImpl) => {
        if (ev != null && ev.eventTypeId === CubiqTS.FxLifeCycleEventType.BULK_REJECTED) {
          this.processBulkRejectedLifecycleEvent(ev);
        } else {
          if (event.orderData && event.orderData.orderState == CubiqTS.OrderState.REJECTED) {
            this.processOrderLifecycleEvent(ev);
          } else {
            let ord: any = this.base.getItem(ev.externalClientReference);
            if (ord) this.base.deleteItemById(ev.externalClientReference);
            this.fireModelDeleteEvent(ev.externalClientReference);
          }
        }
      });
    }
  }

  private processBulkRejectedLifecycleEvent(event: CubiqTS.OrderLifeCycleEventImpl): void {
    let refId: string =
      event.externalClientReference != null ? event.externalClientReference : null;
    let exists: boolean = this.base.getItem(refId) != null;

    if (exists) {
      let order: any = this.base.getItem(refId);
      if (
        order instanceof CubiqTS.LinkedOrderRequestImpl ||
        order instanceof CubiqTS.LinkedOrderModificationRequestImpl
      ) {
        order.rootOrder["orderState"] =
          order["orderState"] =
          order["state"] =
            CubiqTS.OrderState.REJECTED;
      } else if (
        order instanceof CubiqTS.FixingOrderRequestImpl ||
        order instanceof CubiqTS.FixingOrderModificationRequestImpl
      ) {
        order.fixingOrderData["orderState"] =
          order["orderState"] =
          order["state"] =
            CubiqTS.OrderState.REJECTED;
      }

      if (order instanceof CubiqTS.LinkedOrderRequestImpl) {
        let oldId: string = order.rootOrder.externalClientReference;
        let oldSiblingRefId: string =
          order.rootOrder.sibling instanceof CubiqTS.LinkedRelationshipImpl &&
          order.rootOrder.sibling.order instanceof CubiqTS.OrderRequestDataImpl
            ? order.rootOrder.sibling.order.externalClientReference
            : null;
        this.updateExternalReferences(order.rootOrder);
        order.linkReference = order.rootOrder.externalClientReference;
        this.base.replaceItem(oldId, order);
        order.extendedInfo.set("oldClOrdId", oldId);
        order.extendedInfo.set("oldSiblingRefId", oldSiblingRefId);
      } else if (order instanceof CubiqTS.FixingOrderRequestImpl) {
        let oldId: string = order.fixingOrderData.externalClientReference;
        this.updateExternalReferences(order.fixingOrderData);
        this.base.replaceItem(oldId, order);
        order.extendedInfo.set("oldClOrdId", oldId);
      }

      order.extendedInfo.set(
        "errors",
        event.errors != null ? JSON.stringify(event.errors.errorMessages) : null,
      );
      if (this.orderEntryReferences.has(refId)) {
        this.processAndFireOrderEntryEvent(event);
      }

      this.fireModelUpdateEvent(order);
    } else {
      if (this.orderEntryReferences.has(refId)) {
        this.processAndFireOrderEntryEvent(event);
      }

      this.orderEntryReferences.delete(refId);
      this.base.deleteItemById(refId);
      this.fireModelDeleteEvent(refId);
    }
  }

  private processOrderLifecycleEvent(
    event: CubiqTS.OrderLifeCycleEventImpl | CubiqTS.InterventionEventImpl,
  ): void {
    if (event instanceof CubiqTS.OrderLifeCycleEventImpl)
      logger.out.debug(
        "scrdpl-s - OrderLifeCycleEventImpl --> event type " +
          event.eventTypeId +
          " order id: " +
          event.orderId +
          " order data " +
          event.orderData,
      );
    if (event instanceof CubiqTS.InterventionEventImpl)
      logger.out.debug(
        "scrdpl-s - InterventionEventImpl --> event type " +
          event.eventType +
          " order id: " +
          event.orderData.orderId +
          " order data " +
          event.orderData,
      );
    let refId: string = event.orderData != null ? event.orderData.clOrdID : null;

    let exists: boolean = this.base.getItem(refId) != null;

    if (exists && VISIBLE_ORDER_STATES.indexOf(event.orderData.orderState) >= 0) {
      let order: CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl =
        this.base.getItem(refId);

      if (order instanceof CubiqTS.LinkedOrderRequestImpl) {
        order.rootOrder.orderId = event.orderData.orderId.toString();
        order.rootOrder["orderState"] =
          order["orderState"] =
          order["state"] =
            event.orderData.orderState;
      } else if (order instanceof CubiqTS.FixingOrderRequestImpl) {
        order.fixingOrderData.orderId = event.orderData.orderId.toString();
        let fixingOrderData: CubiqTS.FixingOrderRequestDataImpl = order.fixingOrderData;
        fixingOrderData["orderState"] =
          order["orderState"] =
          order["state"] =
            event.orderData.orderState;
      }

      // REPLACING THE ADDITIONAL DATA USAGE
      // SHOULD BE STRIPPED OFF BEFORE SENDING TO THE SERVER
      order.extendedInfo.set(
        COLUMN_DESK_ID,
        event.orderData.deskId != null ? event.orderData.deskId.toString() : null,
      );
      order.extendedInfo.set(
        COLUMN_SALES_GROUP_ID,
        event.orderData.salesGroupId != null ? event.orderData.salesGroupId.toString() : null,
      );
      order.extendedInfo.set(
        COLUMN_ENTRY_USER_ID,
        event.orderData.entryUserId != null ? event.orderData.entryUserId.toString() : null,
      );
      order.extendedInfo.set("orderRef", event.orderData.orderRef);

      if (event.orderData.deskId != null) {
        let traderGroup: CubiqTS.TradingGroupImpl = this.traderGroupLookup.getTraderGroup(
          event.orderData.deskId,
        );
        if (traderGroup != null) {
          if (order instanceof CubiqTS.LinkedOrderRequestImpl) {
            order.rootOrder.traderGroup = traderGroup.groupCode;
          } else if (order instanceof CubiqTS.FixingOrderRequestImpl) {
            order.fixingOrderData.traderGroup = traderGroup.groupCode;
          }
        }
      }

      if (event.orderData.orderState == CubiqTS.OrderState.REJECTED) {
        if (order instanceof CubiqTS.LinkedOrderRequestImpl) {
          let oldId: string = order.rootOrder.externalClientReference;
          let oldSiblingRefId: string =
            order.rootOrder.sibling instanceof CubiqTS.LinkedRelationshipImpl &&
            order.rootOrder.sibling.order instanceof CubiqTS.OrderRequestDataImpl
              ? order.rootOrder.sibling.order.externalClientReference
              : null;
          this.updateExternalReferences(order.rootOrder);
          order.linkReference = order.rootOrder.externalClientReference;
          this.base.replaceItem(oldId, order);
          order.extendedInfo.set("oldClOrdId", oldId);
          if (oldSiblingRefId != null) order.extendedInfo.set("oldSiblingRefId", oldSiblingRefId);
        } else if (order instanceof CubiqTS.FixingOrderRequestImpl) {
          let oldId: string = order.fixingOrderData.externalClientReference;
          this.updateExternalReferences(order.fixingOrderData);
          this.base.replaceItem(oldId, order);
          order.extendedInfo.set("oldClOrdId", oldId);
        }
      }

      order.extendedInfo.set(
        "errors",
        event.errors != null ? JSON.stringify(event.errors.errorMessages) : null,
      );
      this.fireModelUpdateEvent(order);
    } else {
      this.orderEntryReferences.delete(refId);
      this.base.deleteItemById(refId);
      this.fireModelDeleteEvent(refId);
    }
  }

  public addOrderEntryReference(ref: string): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {
      if (!this.orderEntryReferences.has(ref)) {
        this.orderEntryReferences.add(ref);
        this.logOrderEntryDebugInfo(ref, "Scratchpad", ExtendedOrderEntryLogType.LISTENER_ADD);
        res(true);
      } else {
        res(false);
      }
    });
  }

  public removeOrderEntryReference(ref: string): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {
      if (this.orderEntryReferences.has(ref)) {
        this.orderEntryReferences.delete(ref);
        this.logOrderEntryDebugInfo(ref, "Scratchpad", ExtendedOrderEntryLogType.LISTENER_REMOVE);
        res(true);
      } else {
        res(false);
      }
    });
  }

  public hasOrderEntryReference(ref: string): Promise<boolean> {
    return new Promise<boolean>((res, rej) => {
      res(this.orderEntryReferences.has(ref));
    });
  }

  public getOrderData(reference: string): Promise<CubiqTS.OrderDataImpl | CubiqTS.FixingDataImpl> {
    return new Promise<CubiqTS.OrderDataImpl | CubiqTS.FixingDataImpl>((res, rej) => {
      let order: CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl =
        this.base.getItem(reference);
      if (order != null) {
        let orderData: CubiqTS.OrderDataImpl | CubiqTS.FixingDataImpl =
          order instanceof CubiqTS.LinkedOrderRequestImpl
            ? this.createOrderData(order.rootOrder, order)
            : this.createOrderDataFromFixingData(order.fixingOrderData, order);
        res(orderData);
      } else {
        rej("Could not find order with reference " + reference);
      }
    });
  }

  public getOrderDataForOrderId(
    orderId: string,
  ): Promise<CubiqTS.OrderDataImpl | CubiqTS.FixingDataImpl> {
    let ord: any = this.base.getAll().find((item) => {
      if (item instanceof CubiqTS.LinkedOrderRequestImpl) return item.rootOrder.orderId === orderId;
      else if (item instanceof CubiqTS.FixingOrderRequestImpl)
        return item.fixingOrderData.orderId === orderId;
    });
    let refId: string =
      ord instanceof CubiqTS.FixingOrderRequestImpl
        ? ord.fixingOrderData.externalClientReference
        : ord.rootOrder.externalClientReference;
    return this.getOrderData(refId);
  }

  protected createOrderDataFromFixingData(
    request: CubiqTS.FixingOrderRequestDataImpl,
    orderRequest: CubiqTS.FixingOrderRequestImpl,
    skipSibling: boolean = false,
  ): CubiqTS.OrderDataImpl {
    let orderData: CubiqTS.OrderDataImpl = new CubiqTS.OrderDataImpl();
    if (request.orderId) {
      orderData.orderId = CubiqTS.Long.fromString(request.orderId);
      orderData.orderRef = request.orderRef;
    }

    let valueDate: CubiqTS.StandardDateImpl;
    const nearAllocations = request.allocations ?? [];
    const farAllocations = request.farAllocations ?? [];
    orderData.allocationData = nearAllocations
      .concat(farAllocations)
      .map((allocation) => this.mapSingleAllocationRequestData(allocation));
    if (request.fixedAmount != null) {
      orderData.orderQty = new CubiqTS.BigNumber(request.fixedAmount);
    }
    orderData.settleTenor = request.tenor;
    valueDate = request.valueDate;
    orderData.accountCode = request.accountCode;
    orderData.oboAccount = request.oboAccount;
    orderData.clientIntent = request.clientIntent;
    orderData.fixedCode = request.fixedCcy;

    orderData.fwdNettingId = request.fwdNettingId;

    if (valueDate) {
      orderData.settleDate = CubiqTS.moment(
        valueDate.year + "-" + valueDate.month + "-" + valueDate.day,
        "YYYY-MM-DD",
      ).utc();
    }

    orderData.baseCode = request.base;
    orderData.nonBaseCode = request.nonBase;

    orderData.isInvertedOrderFg = false;

    if (this.currencyPairLookup.getCcyPair(request.nonBase, request.base, false) != null) {
      orderData.isInvertedOrderFg = true;
      orderData.baseCode = request.nonBase;
      orderData.nonBaseCode = request.base;
    }

    orderData.channelCd = request.tradingChannel;
    orderData.clOrdID = request.externalClientReference;
    orderData.clientCode = request.clientCode;
    orderData.clientContactIds = request.clientContactIds;
    orderData.comments1 = request.comment;
    orderData.comments2 = request.comment2;

    orderData.oboClient = request.oboClient;

    orderData.customValues = request.customValues;

    if (request.traderGroup != null) {
      let traderGroup: CubiqTS.TradingGroupImpl = this.traderGroupLookup.getTraderGroupByName(
        request.traderGroup,
      );
      if (traderGroup != null) {
        orderData.deskId = traderGroup.groupId;
      }
    }

    if (request.fixingDate != null) {
      let fixingDate: CubiqTS.StandardDateImpl = request.fixingDate;
      orderData.fixingDate = CubiqTS.moment(
        fixingDate.year + "-" + fixingDate.month + "-" + fixingDate.day,
      );
    }

    if (request.fixingTime != null) {
      let fxDetails: CubiqTS.FixingDataImpl = new CubiqTS.FixingDataImpl();
      fxDetails.reqFixingCd = request.fixingSource;
      fxDetails.reqFixingDate = this.standardDateToString(request.fixingDate);
      fxDetails.reqFixingTime = this.standardTimeToString(request.fixingTime);
      fxDetails.reqFixingTzCd = request.fixingTime.timeZone;
      orderData.fixingDetails = fxDetails;
    }

    if (orderRequest.clientTrader != null && orderData.clientCode != null) {
      let users: CubiqTS.UserImpl[] = this.userLookup.getUsersByClientCode(orderData.clientCode);
      let clientTrader: any = users.find(
        (user: CubiqTS.UserImpl) => user.userName == orderRequest.clientTrader,
      );
      if (clientTrader != null) {
        orderData.clientTraderId = clientTrader.userId;
      }
      orderData.clientTraderName = orderRequest.clientTrader;
    }

    orderData.orderType = request.orderType;
    orderData.isPrivate = orderRequest.privateFg;
    orderData.requestedOrderType = request.requestOrderType;
    orderData.product = request.product;
    orderData.reqMarginType = orderData.spotMarkupType = request.markupType;
    orderData.autoForwardPricingFg = request.autoForwardPricing;

    if (request.markup != null) {
      orderData.salesMargin =
        orderData.spotMarkupValue =
        orderData.reqMargin =
          new CubiqTS.BigNumber(request.markup);
    }

    orderData.strategyParameters = request.strategyParameters;
    orderData.stpHold = orderRequest.stpHold;

    if (
      request.strategyParameters != null &&
      request.strategyParameters.length >= SUSPENSION_PARAM_NAME.length
    ) {
      let suspensionParamCount: number = request.strategyParameters.filter(
        (param: CubiqTS.StrategyParameterImpl) =>
          param.parameterType == SUSPENSION_PARAM_TYPE &&
          SUSPENSION_PARAM_NAME.indexOf(param.parameterName) >= 0,
      ).length;
      if (suspensionParamCount == 4) {
        orderData.suspensionDetails = new CubiqTS.SuspensionDetailsImpl();
      }
    }

    if (orderData.clientCode != null) {
      let client: CubiqTS.ClientImpl = this.clientLookupService.getClient(request.clientCode);
      if (client != null) {
        orderData.salesGroupId = client.salesGroupId;
      }
    }

    return orderData;
  }

  /**
   * Need to convert from a Request allocation to a data allocation (so it can be added to an OrderdataImpl
   * and reconverted back into a Request allocation))
   * @param allocation
   */
  private mapSingleAllocationRequestData(
    allocation: CubiqTS.AllocationRequestDataImpl,
  ): CubiqTS.AllocationDataImpl {
    const retData = new CubiqTS.AllocationDataImpl();

    retData.allocationReference = allocation.allocationReferenceId;

    retData.accountCode = allocation.accountCode;
    retData.allocationIntent = allocation.allocationIntent;
    if (allocation.allocationQty) {
      retData.allocationQty = new CubiqTS.BigNumber(allocation.allocationQty);
    }

    retData.fixedCcy = allocation.fixedCcy;
    retData.settleTenor = allocation.settleTenor;

    if (allocation.settleTenor != null && CubiqTS.Tenor.EMPTY !== allocation.settleTenor) {
      retData.settleTenor = allocation.settleTenor;
    } else if (allocation.settleDate != null) {
      // No value date if tenor specified - bulking will recalculate these
      retData.settleDate = this.getSettleDate(allocation);
    }

    return retData;
  }

  // public getOrderData(reference: string): Promise<CubiqTS.OrderDataImpl> {
  //   return new Promise<CubiqTS.OrderDataImpl>((res, rej) => {
  //     let order: CubiqTS.LinkedOrderRequestImpl = this.base.getAll().find(
  //       (item: CubiqTS.LinkedOrderRequestImpl) =>
  //         item instanceof CubiqTS.LinkedOrderRequestImpl &&
  //         item.rootOrder.externalClientReference == reference
  //     ) as CubiqTS.LinkedOrderRequestImpl;

  //     if (order != null) {
  //       let orderData: CubiqTS.OrderDataImpl = this.createOrderData(order.rootOrder, order);
  //       res(orderData);
  //     } else {
  //       rej('Could not find order with reference ' + reference);
  //     }
  //   });
  // }
  private getSettleDate(a: Partial<CubiqTS.AllocationRequestData>): CubiqTS.Moment {
    let date: CubiqTS.Moment = null;
    let settleDate: any = a.settleDate;
    if (settleDate) {
      if (settleDate instanceof Date) {
        date = CubiqTS.moment(settleDate).startOf("day");
      } else if (settleDate instanceof CubiqTS.StandardDateImpl) {
        const dateStr = `${settleDate.year}-${settleDate.month}-${settleDate.day}`;
        date = CubiqTS.moment(dateStr, "YYYY-MM-DD");
      }
    }
    return date;
  }

  protected createOrderData(
    request: CubiqTS.OrderRequestDataImpl,
    orderRequest: CubiqTS.LinkedOrderRequestImpl,
    skipSibling: boolean = false,
  ): CubiqTS.OrderDataImpl {
    let orderData: CubiqTS.OrderDataImpl = new CubiqTS.OrderDataImpl();

    if (request.orderId) {
      orderData.orderId = CubiqTS.Long.fromString(request.orderId);
      orderData.orderRef = request.orderRef;
    }

    orderData.accountCode = request.accountCode;
    orderData.activationDetails = request.activationDetails;

    if (request.activationDetails) {
      orderData.activationAlias = request.activationDetails.timeAlias;
    }

    orderData.expiryDetails = request.expiryDetails;

    if (request.expiryDetails) {
      orderData.expiryAlias = request.expiryDetails.timeAlias;
    }

    orderData.baseCode = request.base;
    orderData.nonBaseCode = request.nonBase;

    orderData.isInvertedOrderFg = false;

    if (this.currencyPairLookup.getCcyPair(request.nonBase, request.base, false) != null) {
      orderData.isInvertedOrderFg = true;
      orderData.baseCode = request.nonBase;
      orderData.nonBaseCode = request.base;
    }

    orderData.channelCd = request.tradingChannel;
    orderData.clOrdID = request.externalClientReference;
    orderData.clOrdLinkID = orderRequest.linkReference;
    orderData.clientCode = request.clientCode;
    orderData.clientContactIds = request.clientContacts;
    orderData.clientIntent = request.clientIntent;
    orderData.comments1 = request.comment;
    orderData.comments2 = request.comment2;

    let trailingStopDetails: CubiqTS.TrailingStopDetails = null;
    if (request.tslDistancePips != null) {
      try {
        trailingStopDetails = new CubiqTS.TrailingStopDetailsImpl();
        let pipSize = this.pricingService.getDecsToPipsMultiplier(orderData);
        trailingStopDetails.trailingDistancePts = new CubiqTS.BigNumber(
          request.tslDistancePips,
        ).dividedBy(pipSize);

        if (request.tslStartRate != null) {
          trailingStopDetails.isInitialTrailingRateProvidedFg = true;
          trailingStopDetails.initialTrailingRt = new CubiqTS.BigNumber(request.tslStartRate);
        }
      } catch (e) {
        trailingStopDetails = null;
      }
    }
    orderData.trailingStopDetails = trailingStopDetails;

    orderData.oboClient = request.oboClient;
    orderData.oboAccount = request.oboAccount;

    orderData.customValues = request.customValues;

    if (request.traderGroup != null) {
      let traderGroup: CubiqTS.TradingGroupImpl = this.traderGroupLookup.getTraderGroupByName(
        request.traderGroup,
      );
      if (traderGroup != null) {
        orderData.deskId = traderGroup.groupId;
      }
    }

    orderData.executionInstruction = request.fillSupport;

    if (
      request.fillTypeRestriction != null &&
      request.fillTypeRestriction != CubiqTS.RequestorFillTypeRestriction.NONE
    ) {
      orderData.executionType =
        request.fillTypeRestriction == CubiqTS.RequestorFillTypeRestriction.AUTO
          ? CubiqTS.ExecutionType.AUTOMATIC
          : CubiqTS.ExecutionType.MANUAL;
    }

    orderData.fixedCode = request.fixedCcy;

    if (request.fixingDate != null) {
      let fixingDate: CubiqTS.StandardDateImpl = request.fixingDate;
      orderData.fixingDate = CubiqTS.moment(
        fixingDate.year + "-" + fixingDate.month + "-" + fixingDate.day,
      );
    }

    orderData.fixingSourceCd = request.fixingSource;

    if (request.fixedAmount != null) {
      orderData.orderQty = new CubiqTS.BigNumber(request.fixedAmount);
    }

    orderData.orderType = request.orderType;
    orderData.isPrivate = request.isPrivateOrder;
    orderData.requestedOrderType = request.requestOrderType;
    orderData.product = request.product;
    orderData.reqMarginType = orderData.spotMarkupType = request.markupType;

    if (request.markup != null) {
      orderData.salesMargin =
        orderData.spotMarkupValue =
        orderData.reqMargin =
          new CubiqTS.BigNumber(request.markup);
    }

    if (request.requestRate) {
      orderData.requestRt = new CubiqTS.BigNumber(request.requestRate);
    }

    orderData.requestTrackingType = request.forwardTrackingType;
    orderData.strategyParameters = request.strategyParameters;
    orderData.settleTenor = request.tenor;

    if (request.valueDate) {
      let valueDate: CubiqTS.StandardDateImpl = request.valueDate;
      orderData.settleDate = CubiqTS.moment(
        valueDate.year + "-" + valueDate.month + "-" + valueDate.day,
      );
    }

    orderData.stpHold = request.stpHold;

    if (orderData.clientCode != null) {
      let client: CubiqTS.ClientImpl = this.clientLookupService.getClient(request.clientCode);
      if (client != null) {
        orderData.salesGroupId = client.salesGroupId;
      }
    }

    if (
      request.strategyParameters != null &&
      request.strategyParameters.length >= SUSPENSION_PARAM_NAME.length
    ) {
      let suspensionParamCount: number = request.strategyParameters.filter(
        (param: CubiqTS.StrategyParameterImpl) =>
          param.parameterType == SUSPENSION_PARAM_TYPE &&
          SUSPENSION_PARAM_NAME.indexOf(param.parameterName) >= 0,
      ).length;
      if (suspensionParamCount == 4) {
        orderData.suspensionDetails = new CubiqTS.SuspensionDetailsImpl();
      }
    }

    if (request.sibling != null && !skipSibling) {
      let sibling: CubiqTS.OrderDataImpl = this.createOrderData(
        request.sibling.order,
        orderRequest,
        true,
      );
      orderData.siblingRelationship = new CubiqTS.Relationship();
      orderData.siblingRelationship.relationshipType = request.sibling.relationshipType;
      orderData.siblingRelationship.order = sibling;

      // Code below creates back link to the root which will in turn result in an endless nesting of models in sibling relationship.
      // Commenting it out seems to result in normalized model, however this change will not be added to 4.8.1 patch. (should be considered for future relaese)
      // On the other hand, with the fix for OMS-36294 order entry will produce the same OE Model strategy structure with normalized structure
      // (for example created in cloneLimitOrder()) and the endlessly nested structure produced by this method.
      sibling.siblingRelationship = new CubiqTS.Relationship();
      sibling.siblingRelationship.relationshipType = request.sibling.relationshipType;
      sibling.siblingRelationship.order = orderData;
    }

    if (request.childRelationships != null && request.childRelationships.length > 0) {
      orderData.childRelationships = request.childRelationships.map(
        (value: CubiqTS.LinkedRelationshipImpl) => {
          let child: CubiqTS.OrderDataImpl = this.createOrderData(value.order, orderRequest);
          let relationship: CubiqTS.Relationship = new CubiqTS.Relationship();
          relationship.relationshipType = value.relationshipType;
          relationship.order = child;

          child.parentRelationship = new CubiqTS.Relationship();
          child.parentRelationship.relationshipType = value.relationshipType;
          child.parentRelationship.order = orderData;

          return relationship;
        },
      );
    }

    return orderData;
  }

  public cloneItems(
    orderIds: string[],
  ): Promise<Array<CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl>> {
    return new Promise<CubiqTS.LinkedOrderRequestImpl[]>((res, rej) => {
      let newItems: CubiqTS.LinkedOrderRequestImpl[] = new Array<CubiqTS.LinkedOrderRequestImpl>();
      orderIds.forEach((orderId: string) => {
        let order: CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl = this.base
          .getAll()
          .find((item: CubiqTS.LinkedOrderRequestImpl | CubiqTS.FixingOrderRequestImpl) => {
            return (
              (item instanceof CubiqTS.LinkedOrderRequestImpl &&
                item.rootOrder.externalClientReference == orderId) ||
              (item instanceof CubiqTS.FixingOrderRequestImpl &&
                item.fixingOrderData.externalClientReference == orderId)
            );
          });
        //logger.out.warn(`DEV-SP: CLONE: Retrieved order (find): `, order);

        if (order == null) {
          order = this.base.getItem(orderId);
        }
        //logger.out.warn(`DEV-SP: CLONE: Retrieved order (get): `, this.base.getItem(orderId));

        let isBenchmark: boolean = order instanceof CubiqTS.FixingOrderRequestImpl;
        let newItem: any = null;

        if (order instanceof CubiqTS.FixingOrderRequestImpl) {
          let fixingOrderItem: CubiqTS.FixingOrderRequestImpl = Object.assign(
            new CubiqTS.FixingOrderRequestImpl(),
            order,
          );
          fixingOrderItem.fixingOrderData = Object.assign(
            new CubiqTS.FixingOrderRequestDataImpl(),
            order.fixingOrderData,
          );
          newItem = fixingOrderItem;
          delete newItem.fixingOrderData.orderState;
        } else {
          let limitOrderCopy: CubiqTS.LinkedOrderRequestImpl = Object.assign(
            new CubiqTS.LinkedOrderRequestImpl(),
            order,
          );
          limitOrderCopy.rootOrder = this.cloneLimitOrder(
            order.rootOrder,
            new CubiqTS.LinkedRelationshipImpl(),
          ).order;
          newItem = limitOrderCopy;
          delete newItem.rootOrder.orderState;
        }

        if (order.extendedInfo != null) {
          newItem.extendedInfo = new Map(order.extendedInfo);
        }

        this.updateExternalReferences(isBenchmark ? newItem.fixingOrderData : newItem.rootOrder);

        if (!isBenchmark) {
          newItem.linkReference = newItem.rootOrder.externalClientReference;
        }

        this.base.mapItem(newItem);
        newItems.push(newItem);
      });
      newItems.forEach(this.fireModelUpdateEvent.bind(this));
      res(newItems);
    });
  }

  private cloneLimitOrder(
    original: CubiqTS.OrderRequestDataImpl,
    copy: CubiqTS.LinkedRelationshipImpl,
    skipSibling: boolean = false,
  ): CubiqTS.LinkedRelationshipImpl {
    // copy orderData
    if (original instanceof CubiqTS.OrderRequestDataImpl) {
      copy.order = Object.assign(new CubiqTS.OrderRequestDataImpl(), original);
      delete (copy.order as any).orderState;
    }
    // copy sibling
    if (
      !skipSibling &&
      original.sibling instanceof CubiqTS.LinkedRelationshipImpl &&
      original.sibling.order instanceof CubiqTS.OrderRequestDataImpl
    ) {
      copy.order.sibling = Object.assign(new CubiqTS.LinkedRelationshipImpl(), original.sibling);
      this.cloneLimitOrder(original.sibling.order, copy.order.sibling, true);
    }
    // copy children
    if (Array.isArray(original.childRelationships) && original.childRelationships.length > 0) {
      copy.order.childRelationships = new Array<CubiqTS.LinkedRelationshipImpl>();
      for (let i = 0; i < original.childRelationships.length; i++) {
        if (
          original.childRelationships[i] instanceof CubiqTS.LinkedRelationshipImpl &&
          original.childRelationships[i].order instanceof CubiqTS.OrderRequestDataImpl
        ) {
          copy.order.childRelationships[i] = Object.assign(
            new CubiqTS.LinkedRelationshipImpl(),
            original.childRelationships[i],
          );
          this.cloneLimitOrder(
            original.childRelationships[i].order,
            copy.order.childRelationships[i],
          );
        }
      }
    }
    return copy;
  }

  /**
   * Create and assign a new unique externalClientReference of the request and any linked orders, removing
   * all existing order references
   *
   * @param order
   * @param skipSibling
   */
  private updateExternalReferences(
    order: CubiqTS.OrderRequestDataImpl | CubiqTS.FixingOrderRequestDataImpl,
    skipSibling: boolean = false,
  ): void {
    order.orderId = "";
    order.orderRef = null;
    order.externalClientReference = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(
      /[xy]/g,
      function (c) {
        const r = (Math.random() * 16) | 0,
          v = c == "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      },
    );
    logger.out.info(`scrdpl-s - Created new order ext reference ${order.externalClientReference}`);

    if (order instanceof CubiqTS.OrderRequestDataImpl) {
      if (
        !skipSibling &&
        order.sibling instanceof CubiqTS.LinkedRelationshipImpl &&
        order.sibling.order instanceof CubiqTS.OrderRequestDataImpl
      ) {
        this.updateExternalReferences(order.sibling.order, true);
        order.sibling.siblingId = null; //order.externalClientReference;
      }

      if (order.childRelationships != null) {
        order.childRelationships
          .filter((value: CubiqTS.LinkedRelationshipImpl) => value.order != null)
          .forEach((value: CubiqTS.LinkedRelationshipImpl) => {
            this.updateExternalReferences(value.order);
            value.parentId = null; //order.externalClientReference;
          });
      }
    }
  }

  public fireModelDeleteEvent(id: string, dispatchUpdate: boolean = true): void {
    super.fireModelDeleteEvent(id);
  }

  private processAndFireOrderEntryEvent(event: any) {
    if (event instanceof CubiqTS.SavedOrderEventImpl) {
      let refId: string = null;
      let ordId: string = null;
      if (event.orderDataRequest != null && event.orderDataRequest.rootOrder != null) {
        refId = event.orderDataRequest.rootOrder.externalClientReference;
        ordId = event.orderDataRequest.rootOrder.orderId;
      } else if (
        event.fixingOrderRequest != null &&
        event.fixingOrderRequest.fixingOrderData != null
      ) {
        refId = event.fixingOrderRequest.fixingOrderData.externalClientReference;
        ordId = event.fixingOrderRequest.fixingOrderData.orderId;
      }
      if (refId != null && this.orderEntryReferences.has(refId)) {
        let metaData: any = {
          orderId: ordId,
          ref: refId,
          state: CubiqTS.OrderState.SAVED,
          messageId: event.messageId,
          errors: event.submissionErrors,
        };
        this.fireModelOrderEntryEvent(metaData, event);
      }
    }

    if (event instanceof CubiqTS.OrderLifeCycleEventImpl) {
      let refId: string = null;
      if (event.eventTypeId === CubiqTS.FxLifeCycleEventType.BULK_REJECTED)
        refId = event.externalClientReference;
      else refId = event.orderData.clOrdID;
      if (refId != null && this.orderEntryReferences.has(refId)) {
        let metaData: any = {};
        if (event.eventTypeId === CubiqTS.FxLifeCycleEventType.BULK_REJECTED) {
          metaData = {
            ref: refId,
            state: CubiqTS.OrderState.REJECTED,
            messageId: event.messageId,
            errors: event.errors,
          };
          this.fireModelOrderEntryEvent(metaData);
        } else {
          metaData = {
            orderId: event.orderData.orderId,
            ref: event.orderData.clOrdID,
            state: event.orderData.orderState,
            messageId: event.messageId,
            errors: event.errors,
          };
          this.fireModelOrderEntryEvent(
            metaData,
            this.base.getItemByReference(event.orderData.clOrdID),
          );
        }
      }
    }

    if (event instanceof CubiqTS.InterventionEventImpl) {
      let interventionEvent: CubiqTS.InterventionEventImpl = event as CubiqTS.InterventionEventImpl;

      let refId: string =
        interventionEvent.orderData != null ? interventionEvent.orderData.clOrdID : null;
      if (refId != null && this.orderEntryReferences.has(refId)) {
        let metaData: any = {
          orderId: interventionEvent.orderData.orderId,
          orderRef: interventionEvent.orderData.orderRef,
          ref: interventionEvent.orderData.clOrdID,
          state: null,
          eventType: null,
          tgcId: null,
          sgcId: null,
          user: null,
          invEvtType: interventionEvent.eventType,
          invOprType: interventionEvent.operationType,
          reasonId: interventionEvent.reason,
          unlockUserId:
            interventionEvent.unLockUserId != null
              ? interventionEvent.unLockUserId.toString()
              : null,
          messageId: interventionEvent.messageId,
          errors: interventionEvent.errors,
        };

        // augment metadata
        let traderGroupCodeId =
          interventionEvent.orderData != null && interventionEvent.orderData.deskId != null
            ? interventionEvent.orderData.deskId.toString()
            : null;
        let salesGroupCodeId =
          interventionEvent.orderData != null && interventionEvent.orderData.salesGroupId != null
            ? interventionEvent.orderData.salesGroupId.toString()
            : null;
        let lockUser =
          interventionEvent.lockUserId != null ? interventionEvent.lockUserId.toString() : null;

        if (
          this.getActiveUser().isTrader() &&
          interventionEvent.eventType === CubiqTS.InterventionEventType.LIMIT_ORDER
        ) {
          metaData.tgcId = traderGroupCodeId;
          metaData.sgcId = salesGroupCodeId;
          metaData.user = lockUser;
        } else if (interventionEvent.operationType === CubiqTS.InterventionOperationType.NEW) {
          if (!this.isVisible(interventionEvent)) {
            this.logOrderEntryDebugInfo(
              interventionEvent,
              "Not visible",
              ExtendedOrderEntryLogType.FORWARD_IGNORE_SIMPLE,
            );
            return;
          }
          metaData.state = interventionEvent.operationType;
          metaData.tgcId = traderGroupCodeId;
          metaData.sgcId = salesGroupCodeId;
          metaData.user = lockUser;
        } else if (
          interventionEvent.operationType === CubiqTS.InterventionOperationType.APPROVE ||
          interventionEvent.operationType === CubiqTS.InterventionOperationType.REJECT
        ) {
          // do not augment metadata
        }
        this.fireModelOrderEntryEvent(metaData, interventionEvent);
      }
    }
  }

  private isVisible(interventionEvent: CubiqTS.InterventionEvent): boolean {
    let activeUser = this.getActiveUser();
    let entryUserCode = null;

    if (interventionEvent == Object(interventionEvent)) {
      if (interventionEvent.orderData == Object(interventionEvent.orderData)) {
        entryUserCode = interventionEvent.orderData.entryUserCode;
      }

      return activeUser.canSeeIntervention(
        entryUserCode,
        interventionEvent.traderGroupId ? interventionEvent.traderGroupId.toString() : null,
        interventionEvent.salesGroupId ? interventionEvent.salesGroupId.toString() : null,
        interventionEvent.reason,
        interventionEvent.eventType,
      );
    }

    return false;
  }

  private getActiveUser(): ActiveUser {
    return this.activeUser;
  }

  protected fireModelUpdateEvent(
    rawData: any,
    additionalData: any = null,
    dispatchUpdate: boolean = true,
  ): any {
    super.fireModelUpdateEvent(rawData, null, true);
    return null;
  }

  private standardDateToString(dt: CubiqTS.StandardDateImpl): string {
    let yr: string = dt.year.toString();
    let mth: string =
      dt.month.toString().length == 2 ? dt.month.toString() : "0" + dt.month.toString();
    let dy: string = dt.day.toString().length == 2 ? dt.day.toString() : "0" + dt.day.toString();
    return yr + mth + dy;
  }

  private standardTimeToString(tm: CubiqTS.StandardTimeImpl): string {
    let hr: string =
      tm.hours.toString().length == 2 ? tm.hours.toString() : "0" + tm.hours.toString();
    let mn: string =
      tm.minutes.toString().length == 2 ? tm.minutes.toString() : "0" + tm.minutes.toString();
    return hr + mn;
  }

  public deleteSibling(siblingReference: string): void {
    this.base.deleteItemById(siblingReference);
  }

  /**
   * From an existing OrderRequest and a member allocation, create a new OrderRequest with
   * the exact same data except with allocation-specific values only
   * @param request
   * @param allocation
   */
  public cloneBMOrderFromAllocation(
    request: CubiqTS.FixingOrderRequestImpl,
    allocation: CubiqTS.AllocationRequestData,
  ): CubiqTS.FixingOrderRequestImpl {
    logger.out.info(`scrdpl-s - cloning new order from allocation`);

    const newRequest = Object.assign(new CubiqTS.FixingOrderRequestImpl(), request);
    const newOrder = Object.assign(
      new CubiqTS.FixingOrderRequestDataImpl(),
      request.fixingOrderData,
      { orderState: null },
    );

    newOrder.allocations = [allocation];
    newOrder.accountCode = allocation.accountCode;
    newOrder.fixedAmount = allocation.allocationQty;
    newOrder.clientIntent = allocation.allocationIntent;
    newOrder.markup = allocation.markupQty;
    newOrder.markupType = allocation.markupType;
    newOrder.tenor = allocation.settleTenor;
    newOrder.valueDate = allocation.settleDate;
    newOrder.oboAccount = allocation.oboAccount;
    newOrder.product =
      allocation.settleTenor === CubiqTS.Tenor.SPOT ? CubiqTS.Product.SPT : CubiqTS.Product.FWD;

    // Remove any existing swap-level properties
    newOrder.swapFixedAmount = null;
    newOrder.swapTenor = null;
    newOrder.swapValueDate = null;
    newOrder.farAllocations = [];
    newRequest.fixingOrderData = newOrder;
    this.updateExternalReferences(newOrder);
    return newRequest;
  }
}
